import { api } from './api'

// Publishing writes a markdown file into the product repo. The post goes live
// with the next production release, not immediately — every message in this UI
// has to say so, or authors will refresh the site waiting for it.

export const BLOG_CATEGORIES = [
  'Announcements',
  'Case studies',
  'Technical',
  'Tutorials',
] as const

export type BlogCategory = (typeof BLOG_CATEGORIES)[number]

export interface FaqItem {
  question: string
  answer: string
}

export interface TocEntry {
  id: string
  label: string
}

export interface UploadedImage {
  filename: string
  base64: string
}

/** Everything an author supplies. Reading time and TOC are derived by the server. */
export interface PostDraft {
  /** Body images — imported from Google Docs or uploaded by hand — committed with the post. */
  images?: UploadedImage[]
  /**
   * A cover picked in the editor. Committed to `content/covers/` before the
   * post, which is what lets `cover` name it safely: the web build `import`s
   * cover art, so a name with no file behind it fails the production build.
   */
  coverImage?: UploadedImage
  slug?: string
  title: string
  date: string
  category: BlogCategory
  author: string
  excerpt: string
  body: string
  faq?: FaqItem[]
  cover?: string
  /** Alt text for the cover. Empty means decorative, which is a valid choice. */
  coverAlt?: string
  featured?: boolean
  expectedSha?: string
}

export interface PostSummary {
  slug: string
  sha: string
  valid: boolean
  title?: string
  date?: string
  category?: BlogCategory
  author?: string
  excerpt?: string
  featured?: boolean
  readingMinutes?: number
  error?: string
}

/** An image pulled out of a Google Docs export, not yet committed. */
export interface DraftImage {
  /** Filename it will be committed as, e.g. `my-post-1.png`. */
  filename: string
  /** Path the body references, e.g. `/blog-images/my-post-1.png`. */
  url: string
  /** Full data URI, so the preview can show it before it exists in the repo. */
  dataUri: string
  bytes: number
  width?: number
  height?: number
  /** Narrower than the published content column, so it will look soft. */
  lowRes?: boolean
}

export interface ConversionResult {
  fields: Omit<PostDraft, 'slug' | 'expectedSha'>
  slug: string
  images: DraftImage[]
  /** What the AI dropped — editorial metadata, notes to the writer. */
  removed: string[]
  warnings: string[]
}

export interface FieldError {
  field: string
  message: string
}

export interface ValidationResult {
  valid: boolean
  slug?: string
  path?: string
  readingMinutes?: number
  toc?: TocEntry[]
  errors?: FieldError[]
}

export interface LoadedPost {
  post: {
    slug: string
    title: string
    date: string
    category: BlogCategory
    author: string
    excerpt: string
    featured: boolean
    faq: FaqItem[]
    body: string
    coverFile?: string
    coverAlt?: string
  } | null
  raw: string
  sha: string
  error?: string
}

const BASE = '/admin/blog'

/** Pulls a readable message out of an axios error, falling back to its own text. */
export function messageOf(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { message?: string } } })?.response
  return response?.data?.message ?? (error as Error)?.message ?? fallback
}

export const blogApi = {
  async config(): Promise<{ configured: boolean; categories: BlogCategory[] }> {
    const { data } = await api.get(`${BASE}/config`)
    return data
  },

  async list(): Promise<PostSummary[]> {
    const { data } = await api.get(`${BASE}/posts`)
    return data.posts
  },

  async get(slug: string): Promise<LoadedPost> {
    const { data } = await api.get(`${BASE}/posts/${slug}`)
    return data
  },

  /**
   * A committed cover, as a data URI. Covers are compiled into the site rather
   * than served from it, so there is no URL to load one from directly — and a
   * null here means the file is gone, which would fail the next web build.
   */
  async cover(filename: string): Promise<{ filename: string; dataUri: string; bytes: number } | null> {
    try {
      const { data } = await api.get(`${BASE}/covers/${encodeURIComponent(filename)}`)
      return data
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) return null
      throw error
    }
  },

  /**
   * Full Google Docs pipeline: images are extracted from the base64 payload in
   * code, the remaining prose goes to a model for cleanup, and the images are
   * reattached to the result. Slow (one model call), so the caller must show
   * progress.
   *
   * The timeout allows for the server working through its model fallbacks: a
   * provider that hangs rather than failing burns its own timeout before the
   * next model is tried, and giving up here first would replace the server's
   * explanation with a bare "timeout".
   */
  async convert(raw: string, slug?: string): Promise<ConversionResult> {
    const { data } = await api.post(`${BASE}/convert`, { raw, slug }, { timeout: 330_000 })
    return data
  },

  /**
   * Dry run against the same parser the web build uses. A pass here means the
   * post cannot fail the production build.
   */
  async validate(draft: PostDraft): Promise<ValidationResult> {
    try {
      const { data } = await api.post(`${BASE}/validate`, draft)
      return data
    } catch (error) {
      const data = (error as { response?: { data?: ValidationResult } })?.response?.data
      if (data && typeof data.valid === 'boolean') return data
      return { valid: false, errors: [{ field: '', message: messageOf(error, 'Validation failed') }] }
    }
  },

  async save(draft: PostDraft): Promise<{ slug: string; path: string; sha: string; message: string }> {
    const { data } = await api.post(`${BASE}/posts`, draft)
    return data
  },

  async remove(slug: string): Promise<{ message: string }> {
    const { data } = await api.delete(`${BASE}/posts/${slug}`)
    return data
  },
}
